'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { 
  Phone, 
  MapPin, 
  ShoppingCart, 
  User, 
  Menu, 
  X, 
  Flame, 
  Wind, 
  Sun, 
  Wrench, 
  FileText,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  Search
} from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useCurrency } from '@/context/CurrencyContext';
import { ThemeToggle } from '@/components/common/ThemeToggle';

interface HeaderProps {
  bannerEnabled?: boolean;
  bannerText?: string;
  bannerBadge?: string;
  bannerLink?: string;
  companyName?: string;
  companyNipt?: string;
  companyPhone?: string;
  companyCity?: string;
  companyCountry?: string;
}

export function Header({
  bannerEnabled = true,
  bannerText = '🔥 Ofertë Sezonale: 15% Ulje në Pompat e Nxehtësisë Daikin & Gree + Konsulencë Falas!',
  bannerBadge = 'OFERTË SPECIALE',
  bannerLink = '/offers',
  companyName = 'DYRMA Climate',
  companyNipt = 'M42708202R',
  companyPhone = '+355 69 367 3867',
  companyCity = 'Elbasan',
  companyCountry = 'Albania',
}: HeaderProps) {
  const pathname = usePathname();
  const { itemCount } = useCart();
  const { currency, setCurrency, defaultCurrency, secondaryCurrency } = useCurrency();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    if (path === '/') return pathname === '/';
    return pathname.startsWith(path);
  };

  return (
    <header className="w-full bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-b border-slate-200/80 dark:border-slate-800/80 sticky top-0 z-50 transition-colors duration-150">
      {/* 0. ANNOUNCEMENT TOP BANNER (Configurable via Admin) */}
      {bannerEnabled && bannerText && (
        <div className="bg-gradient-to-r from-sky-600 via-blue-600 to-indigo-600 text-white text-xs py-1.5 px-4 text-center font-medium shadow-sm transition-all">
          <div className="max-w-7xl mx-auto flex items-center justify-center gap-2 flex-wrap">
            {bannerBadge && (
              <span className="px-2 py-0.5 rounded-full bg-white/20 text-white text-[10px] font-extrabold uppercase tracking-wider backdrop-blur-sm">
                {bannerBadge}
              </span>
            )}
            <span className="text-white/95 text-xs font-medium">{bannerText}</span>
            {bannerLink && (
              <Link
                href={bannerLink}
                className="underline font-bold text-white hover:text-sky-100 inline-flex items-center gap-0.5 ml-1 text-xs"
              >
                Përfito Tani <ArrowRight className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* 1. TOP UTILITY BAR */}
      <div className="bg-slate-900 dark:bg-slate-950 text-slate-300 text-xs py-2 px-4 sm:px-8 border-b border-slate-800/60">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between items-center gap-2">
          <div className="flex items-center space-x-5">
            <span className="flex items-center gap-1.5 text-emerald-400 font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
              {companyName} &mdash; NIPT: {companyNipt}
            </span>
            <span className="hidden md:flex items-center gap-1 text-slate-400">
              <MapPin className="w-3.5 h-3.5 text-sky-400" />
              {companyCity}, {companyCountry}
            </span>
            <a
              href={`tel:${companyPhone.replace(/\s+/g, '')}`}
              className="flex items-center gap-1 text-slate-300 hover:text-white transition-colors"
            >
              <Phone className="w-3.5 h-3.5 text-sky-400" />
              {companyPhone}
            </a>
          </div>

          <div className="flex items-center space-x-3">
            {/* Currency switcher */}
            <div className="flex items-center gap-1 bg-slate-800 dark:bg-slate-900 rounded-lg px-2 py-0.5 border border-slate-700">
              <span className="text-[11px] text-slate-400">Monedha:</span>
              <button
                onClick={() => setCurrency(defaultCurrency)}
                className={`text-xs px-1.5 py-0.5 rounded font-bold transition-colors ${
                  currency === defaultCurrency ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {defaultCurrency}
              </button>
              <button
                onClick={() => setCurrency(secondaryCurrency)}
                className={`text-xs px-1.5 py-0.5 rounded font-bold transition-colors ${
                  currency === secondaryCurrency ? 'bg-sky-600 text-white' : 'text-slate-400 hover:text-white'
                }`}
              >
                {secondaryCurrency}
              </button>
            </div>

            {/* Theme Toggle in Top Utility Bar */}
            <ThemeToggle variant="dropdown" />
          </div>
        </div>
      </div>

      {/* 2. MAIN NAVIGATION BAR */}
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-3.5 flex items-center justify-between gap-4">
        {/* Brand Logo */}
        <Link href="/" className="flex items-center gap-2.5 flex-shrink-0 group">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-sky-600 via-blue-600 to-indigo-600 flex items-center justify-center text-white shadow-md shadow-sky-500/25 group-hover:scale-105 transition-transform duration-200">
            <Wind className="w-6 h-6" />
          </div>
          <div>
            <div className="text-xl font-black tracking-tight text-slate-900 dark:text-white leading-none">
              DYRMA <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-blue-600 dark:from-sky-400 dark:to-blue-400">CLIMATE</span>
            </div>
            <div className="text-[10px] text-slate-500 dark:text-slate-400 font-medium tracking-wider uppercase mt-0.5">
              Klimatizim &bull; Ngrohje &bull; Energji
            </div>
          </div>
        </Link>

        {/* Desktop Nav Links */}
        <nav className="hidden lg:flex items-center gap-1 text-sm font-medium text-slate-700 dark:text-slate-300">
          <Link
            href="/shop"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/shop')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Katalogu
          </Link>
          <Link
            href="/category/kondicionere"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/category/kondicionere')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Kondicionerë
          </Link>
          <Link
            href="/category/pompa-nxehtesie"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/category/pompa-nxehtesie')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Pompa Nxehtësie
          </Link>
          <Link
            href="/category/energji-diellore"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/category/energji-diellore')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Energji Diellore
          </Link>
          <Link
            href="/services"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/services')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Shërbime
          </Link>
          <Link
            href="/projects"
            className={`px-3 py-2 rounded-xl transition-all hover:text-sky-600 dark:hover:text-sky-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 ${
              isActive('/projects')
                ? 'text-sky-600 dark:text-sky-400 font-bold bg-sky-50 dark:bg-sky-950/60 border border-sky-100 dark:border-sky-900/50'
                : ''
            }`}
          >
            Projektet
          </Link>
          <Link
            href="/offers"
            className={`px-3 py-2 rounded-xl transition-all hover:text-rose-600 dark:hover:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/40 ${
              isActive('/offers')
                ? 'text-rose-600 dark:text-rose-400 font-bold bg-rose-50 dark:bg-rose-950/60 border border-rose-200 dark:border-rose-900/50'
                : 'text-rose-600 dark:text-rose-400 font-semibold'
            }`}
          >
            <span className="flex items-center gap-1">
              <Sparkles className="w-3.5 h-3.5 text-rose-500" />
              Ofertat
            </span>
          </Link>

          <Link
            href="/request-quote"
            className="ml-2 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-xl bg-gradient-to-r from-sky-50 to-blue-50 dark:from-sky-950/80 dark:to-blue-950/80 text-sky-700 dark:text-sky-300 hover:from-sky-100 hover:to-blue-100 dark:hover:from-sky-900 dark:hover:to-blue-900 font-bold text-xs transition-all border border-sky-200 dark:border-sky-800 shadow-sm"
          >
            <FileText className="w-3.5 h-3.5 text-sky-600 dark:text-sky-400" />
            Kërko Preventiv
          </Link>
        </nav>

        {/* Right Action Icons: Search, Cart & Account */}
        <div className="flex items-center gap-2 sm:gap-3">
          {/* Quick search shortcut button */}
          <Link
            href="/shop"
            className="p-2 rounded-xl text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-sky-600 dark:hover:text-sky-400 transition-colors"
            title="Kërko produkte"
          >
            <Search className="w-4 h-4" />
          </Link>

          <Link
            href="/account"
            className="flex items-center gap-2 p-1.5 sm:px-2.5 sm:py-1.5 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border border-transparent hover:border-slate-200 dark:hover:border-slate-700"
            title="Llogaria Ime"
          >
            <div className="w-8 h-8 rounded-lg bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-600 dark:text-slate-300 border border-slate-200 dark:border-slate-700">
              <User className="w-4 h-4" />
            </div>
            <span className="hidden sm:inline text-xs font-semibold">Llogaria</span>
          </Link>

          <Link
            href="/cart"
            className="relative flex items-center gap-2 px-3 py-2 rounded-xl bg-slate-900 dark:bg-sky-600 text-white hover:bg-slate-800 dark:hover:bg-sky-500 transition-all shadow-md shadow-slate-950/10 dark:shadow-sky-600/20"
          >
            <ShoppingCart className="w-4 h-4 text-sky-400 dark:text-white" />
            <span className="text-xs font-bold hidden sm:inline">Shporta</span>
            {itemCount > 0 && (
              <span className="bg-sky-500 dark:bg-white text-white dark:text-sky-700 text-[11px] font-black px-1.5 py-0.2 rounded-full min-w-5 text-center shadow-sm">
                {itemCount}
              </span>
            )}
          </Link>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 rounded-xl text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6 text-slate-800 dark:text-white" />}
          </button>
        </div>
      </div>

      {/* 3. MOBILE MENU DRAWER */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 px-4 pt-3 pb-6 space-y-3 shadow-2xl animate-in slide-in-from-top-2">
          {/* Theme & Currency Controls for mobile */}
          <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
            <span className="text-xs font-bold text-slate-500 dark:text-slate-400">Pamja e Sistemit:</span>
            <ThemeToggle variant="segmented" />
          </div>

          <div className="grid grid-cols-2 gap-2 pb-2 text-xs">
            <Link
              href="/shop"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2.5 rounded-xl bg-slate-50 dark:bg-slate-800 text-slate-800 dark:text-slate-100 font-semibold flex items-center gap-2 border border-slate-200/60 dark:border-slate-700"
            >
              <Wind className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              Të Gjitha Produktet
            </Link>
            <Link
              href="/request-quote"
              onClick={() => setMobileMenuOpen(false)}
              className="p-2.5 rounded-xl bg-sky-50 dark:bg-sky-950/60 text-sky-700 dark:text-sky-300 font-semibold flex items-center gap-2 border border-sky-100 dark:border-sky-800"
            >
              <FileText className="w-4 h-4 text-sky-600 dark:text-sky-400" />
              Kërko Preventiv
            </Link>
          </div>

          <div className="space-y-1 font-medium text-slate-800 dark:text-slate-200 text-sm">
            <Link
              href="/category/kondicionere"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Wind className="w-4 h-4 text-sky-500" />
              Kondicionerë & Sisteme Split
            </Link>
            <Link
              href="/category/pompa-nxehtesie"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Flame className="w-4 h-4 text-rose-500" />
              Pompa Nxehtësie (Heat Pumps)
            </Link>
            <Link
              href="/category/energji-diellore"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Sun className="w-4 h-4 text-amber-500" />
              Energji Diellore & Bojlera
            </Link>
            <Link
              href="/services"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <Wrench className="w-4 h-4 text-emerald-500" />
              Shërbime Teknike & Montim
            </Link>
            <Link
              href="/projects"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              <ShieldCheck className="w-4 h-4 text-blue-500" />
              Portofoli i Projekteve
            </Link>
            <Link
              href="/offers"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-rose-50 dark:hover:bg-rose-950/40 text-rose-600 dark:text-rose-400 font-bold transition-colors"
            >
              <Sparkles className="w-4 h-4 text-rose-500" />
              Ofertat & Uljet Sezonale
            </Link>
            <Link
              href="/about"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Rreth Nesh
            </Link>
            <Link
              href="/contact"
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
            >
              Kontakt & Vendndodhja
            </Link>
          </div>

          <div className="pt-3 border-t border-slate-100 dark:border-slate-800 flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
            <span>Urgjenca teknike & shitjet: <strong>{companyPhone}</strong></span>
          </div>
        </div>
      )}
    </header>
  );
}
